/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as $ from "jquery";
import {UiField} from "./UiField";
import {UiFileFieldDisplayType} from "../../generated/UiFileFieldDisplayType";
import {SubEventFactory} from "../../generated/SubEventFactory";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {generateUUID, humanReadableFileSize, Renderer} from "../Common";
import {
	UiFileField_FileClickedSubEvent,
	UiFileField_FileRemoveButtonClickedSubEvent,
	UiFileField_FileUploadedSubEvent,
	UiFileField_FileUploadErrorSubEvent,
	UiFileFieldConfig, UiFileFieldSubCommandHandler, UiFileFieldSubEventSource
} from "../../generated/UiFileFieldConfig";
import {TeamAppsEvent} from "../util/TeamAppsEvent";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";
import {UiTemplateConfig} from "../../generated/UiTemplateConfig";
import {StaticIcons} from "../util/StaticIcons";
import {ProgressIndicator} from "../micro-components/ProgressIndicator";
import {ProgressCircle} from "../micro-components/ProgressCircle";
import {ProgressBar} from "../micro-components/ProgressBar";
import * as log from "loglevel";
import {Logger} from "loglevel";
import {keyCodes} from "trivial-components";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class UiFileField extends UiField<UiFileFieldConfig, any[]> implements UiFileFieldSubEventSource, UiFileFieldSubCommandHandler {
	onFileUploaded: TeamAppsEvent<UiFileField_FileUploadedSubEvent> = new TeamAppsEvent<UiFileField_FileUploadedSubEvent>(this);
	onFileUploadError: TeamAppsEvent<UiFileField_FileUploadErrorSubEvent> = new TeamAppsEvent<UiFileField_FileUploadErrorSubEvent>(this);
	onFileClicked: TeamAppsEvent<UiFileField_FileClickedSubEvent> = new TeamAppsEvent<UiFileField_FileClickedSubEvent>(this);
	onFileRemoveButtonClicked: TeamAppsEvent<UiFileField_FileRemoveButtonClickedSubEvent> = new TeamAppsEvent<UiFileField_FileRemoveButtonClickedSubEvent>(this);

	private $wrapper: JQuery;
	private $uploadButton: JQuery;
	private $fileSelect: JQuery;
	private $uploadButtonTemplate: JQuery;
	private fileItems: FileItem[];
	private itemRenderer: Renderer;

	private displayType: UiFileFieldDisplayType;
	private maxFiles: number;
	private maxBytesPerFile: number;
	private uploadButtonData: any;
	private uploadButtonTemplate: UiTemplateConfig;
	private uploadUrl: string;
	private $fileList: JQuery;

	protected initialize(config: UiFileFieldConfig, targetElement: Element, context: TeamAppsUiContext) {
		this.fileItems = [];
		let uuid = "ui-file-field" + generateUUID();
		this.$wrapper = $(`<div class="UiFileField" id="${uuid}">
        <div class="list"></div>
        <div class="upload-button-wrapper" tabindex="0">
            <div class="upload-button-template"></div>
            <input class="upload-button" type="file" multiple tabindex="-1">
        </div>
</div>`);

		this.$wrapper
			.on("dragover", (e) => {
				// preventDefault() is important as it indicates that the drop is possible!!! see https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Drag_operations#droptargets
				e.preventDefault();
				this.$wrapper.addClass("drop-zone-active");
			})
			.on("dragleave", (e) => {
				this.$wrapper.removeClass("drop-zone-active");
				e.preventDefault();
			})
			.on("dragend", (e) => {
				this.$wrapper.removeClass("drop-zone-active");
				e.preventDefault();
			});
		this.$wrapper[0].ondrop = (e) => { // I don't know why I cannot just register this the same way as the other handlers...
			e.stopPropagation();
			e.preventDefault();
			this.$wrapper.removeClass("drop-zone-active");
			const files = e.dataTransfer.files;
			this.handleFiles(files);
		};

		this.$uploadButtonTemplate = this.$wrapper.find('.upload-button-template');
		this.$uploadButton = this.$wrapper.find('.upload-button-wrapper')
			.keydown(e => {
				if (e.keyCode === keyCodes.enter || e.keyCode === keyCodes.space) {
					this.$fileSelect.click();
					return false; // no scrolling when space is pressed!
				}
			});
		this.$fileSelect = this.$wrapper.find('.upload-button');
		this.$fileSelect.change(() => {
			const files = (<HTMLInputElement> this.$fileSelect[0]).files;
			this.handleFiles(files);
		});

		this.$fileList = this.$wrapper.find('.list');

		this.setItemTemplate(config.itemTemplate);
		this.setMaxBytesPerFile(config.maxBytesPerFile);
		this.setShowEntriesAsButtonsOnHover(config.showEntriesAsButtonsOnHover);
		this.setUploadButtonTemplate(config.uploadButtonTemplate);
		this.setUploadButtonData(config.uploadButtonData);
		this.setUploadUrl(config.uploadUrl);
		this.setDisplayType(config.displayType);
		this.setMaxFiles(config.maxFiles);

		this.$wrapper.appendTo(targetElement);
	}

	isValidData(v: any[]): boolean {
		return v == null || Array.isArray(v);
	}

	public setItemTemplate(itemTemplate: UiTemplateConfig): void {
		this.itemRenderer = this._context.templateRegistry.createTemplateRenderer(itemTemplate);
		this.displayCommittedValue();
	}

	setDisplayType(displayType: UiFileFieldDisplayType): void {
		this.displayType = displayType;
		this.$wrapper.toggleClass("float-style-vertical-list", displayType === UiFileFieldDisplayType.LIST);
		this.$wrapper.toggleClass("float-style-horizontal", displayType === UiFileFieldDisplayType.FLOATING);
	}

	setMaxFiles(maxFiles: number): void {
		this.maxFiles = maxFiles || Number.MAX_SAFE_INTEGER;
		this.updateVisibilities();
	}

	public setMaxBytesPerFile(maxBytesPerFile: number): void {
		this.maxBytesPerFile = maxBytesPerFile;
	}

	public setShowEntriesAsButtonsOnHover(showEntriesAsButtonsOnHover: boolean): void {
		this.$wrapper.toggleClass("show-entries-as-buttons-on-hover", showEntriesAsButtonsOnHover);
	}

	public setUploadButtonData(uploadButtonData: any): void {
		this.uploadButtonData = uploadButtonData;
		this.$uploadButtonTemplate
			.empty()
			.append($(this._context.templateRegistry.createTemplateRenderer(this.uploadButtonTemplate).render(this.uploadButtonData)));
	}

	public setUploadButtonTemplate(uploadButtonTemplate: UiTemplateConfig): void {
		this.uploadButtonTemplate = uploadButtonTemplate;
		this.$uploadButtonTemplate
			.empty()
			.append($(this._context.templateRegistry.createTemplateRenderer(this.uploadButtonTemplate).render(this.uploadButtonData)));
	}

	public setUploadUrl(uploadUrl: string): void {
		this.uploadUrl = uploadUrl;
	}


	private handleFiles(files: FileList) {
		let numberOfFilesToAdd = files.length;
		if (this.maxFiles && this.numberOfNonErrorFileItems + files.length > this.maxFiles) {
			numberOfFilesToAdd = this.maxFiles - this.numberOfNonErrorFileItems;
		}

		for (let i = 0; i < numberOfFilesToAdd; i++) {
			let fileItem = this.createFileItem();
			fileItem.getMainDomElement().appendTo(this.$fileList);
			this.fileItems.push(fileItem);
			fileItem.upload(files[i]);
		}

		this.updateVisibilities();
		this.resetFileInput();
	}

	private get numberOfNonErrorFileItems() {
		return this.fileItems.filter(fileItem => fileItem.state !== FileItemState.ERROR).length;
	}

	private resetFileInput() {
		this.$fileSelect.val('').replaceWith(this.$fileSelect = this.$fileSelect.clone(true));
	}

	private updateVisibilities() {
		this.$uploadButton.toggle(this.isEditable() && this.numberOfNonErrorFileItems < this.maxFiles);
	}

	public getMainDomElement(): JQuery {
		return this.$wrapper;
	}

	public getFocusableElement(): JQuery {
		return this.$uploadButton;
	}

	protected displayCommittedValue(): void {
		let uiValue = this.getCommittedValue();
		if (!uiValue) {
			this.fileItems
				.filter(fileItem => fileItem.state === FileItemState.DISPLAYING)
				.forEach(fileItem => {
					this.removeFileItem(fileItem);
				});
		} else {
			this.fileItems
				.filter(fileItem => fileItem.state === FileItemState.DISPLAYING)
				.forEach(unmatchedFileItem => {
					this.removeFileItem(unmatchedFileItem);
				});
			uiValue.slice().reverse().forEach(record => {
				let fileItem = this.createFileItem();
				fileItem.data = record;
				fileItem.getMainDomElement().prependTo(this.$fileList);
				this.fileItems.push(fileItem);
			});
		}
		this.updateVisibilities();
	}

	replaceFileItem(uuid: string, data: any): void {
		let correspondingItem = uuid && this.fileItems.filter(fileItem => fileItem.uuid === uuid)[0];
		if (correspondingItem) {
			correspondingItem.data = data;
			this.commit();
		} else {
			this.logger.info("Could not replace non-existing file item: " + uuid);
		}
	}

	private removeFileItem(fileItem: FileItem) {
		fileItem.getMainDomElement().remove();
		this.fileItems = this.fileItems.filter(fileItem => fileItem !== fileItem);
	}

	private createFileItem() {
		let fileItem = new FileItem(this.displayType === UiFileFieldDisplayType.FLOATING, this.maxBytesPerFile, this._config.fileTooLargeMessage, this._config.uploadErrorMessage, this.uploadUrl, this.itemRenderer);
		fileItem.onClick.addListener((eventObject, emitter) => {
			this.onFileClicked.fire(SubEventFactory.createUiFileField_FileClickedSubEvent((emitter as FileItem).data));
		});
		fileItem.onDeleteButtonClick.addListener((eventObject, emitter) => {
			let fileItem = emitter as FileItem;
			let data = (fileItem).data;
			if (data) {
				this.onFileRemoveButtonClicked.fire(SubEventFactory.createUiFileField_FileRemoveButtonClickedSubEvent((data)));
			}
			this.removeFileItem(fileItem);
			this.updateVisibilities();
			if (fileItem.state === FileItemState.DISPLAYING) {
				this.commit();
			}
		});
		fileItem.onUploadSuccess.addListener((subEvent) => {
			this.onFileUploaded.fire(subEvent);
			this.updateVisibilities();
		});
		fileItem.onUploadError.addListener((subEvent) => {
			this.onFileUploadError.fire(subEvent);
			this.updateVisibilities();
		});
		return fileItem;
	}

	public getTransientValue(): any[] {
		return this.fileItems
			.filter(fileItem => fileItem.state === FileItemState.DISPLAYING)
			.map(fileItem => fileItem.data);
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		UiField.defaultOnEditingModeChangedImpl(this);
		this.updateVisibilities();
	}

	destroy(): void {
		this.$wrapper.remove();
	}

	public static getReadOnlyHtml(config: UiFileFieldConfig, value: any[], context: TeamAppsUiContext, drawBorder: boolean): string {
		let content: string;
		if (value != null) {
			let templateRenderer = context.templateRegistry.createTemplateRenderer(config.itemTemplate);
			content = value.map((entry) => templateRenderer.render(entry)).join("");
		} else {
			content = "";
		}
		return `<div class="static-readonly-UiFileField ${drawBorder ? "bordered" : ""}">${content}</div>`
	}

	getDefaultValue(): any[] {
		return [];
	}

	public valuesChanged(v1: any[], c2: any[]): boolean {
		return true; // TODO pojos
	}
}

enum FileItemState {
	UPLOADING = "uploading",
	ERROR = "error",
	SUCCESS = "success",
	DISPLAYING = "displaying"
}

class FileItem {
	private static LOGGER: Logger = log.getLogger("FileItem");
	public readonly onUploadSuccess: TeamAppsEvent<UiFileField_FileUploadedSubEvent> = new TeamAppsEvent<UiFileField_FileUploadedSubEvent>(this);
	public readonly onUploadError: TeamAppsEvent<UiFileField_FileUploadErrorSubEvent> = new TeamAppsEvent<UiFileField_FileUploadErrorSubEvent>(this);
	public readonly onDeleteButtonClick: TeamAppsEvent<void> = new TeamAppsEvent<void>(this);
	public readonly onClick: TeamAppsEvent<void> = new TeamAppsEvent<void>(this);

	private $main: JQuery;

	private _data: any;
	private _state: FileItemState | null;
	private $fileInfo: JQuery;
	private $progressIndicator: JQuery;
	private $deleteButton: JQuery;
	private $fileSize: JQuery;
	private $fileName: JQuery;
	private _uuid: String;
	private progressIndicator: ProgressIndicator;
	private $templateWrapper: JQuery;

	private xhr: JQuery.jqXHR;

	constructor(private renderAsCircle: boolean, private maxBytes: number, private fileTooLargeMessage: string, private uploadErrorMessage: string, private uploadUrl: string, private renderer: Renderer) {
		this.$main = $(`<div class="file-item">
			<div class="progress-indicator"></div>
			<div class="file-info">
				<div class="file-name"></div>
				<div class="file-size"></div>
			</div>
			<div class="template-wrapper"></div>
			<img class="delete-button img img-16" alt="delete" tabindex="0" src="${StaticIcons.CLOSE}"/>
		</div>`);
		this.$fileInfo = this.$main.find(".file-info");
		this.$fileName = this.$main.find(".file-name");
		this.$fileSize = this.$main.find(".file-size");
		this.$templateWrapper = this.$main.find(".template-wrapper");
		this.$progressIndicator = this.$main.find(".progress-indicator");
		this.$deleteButton = this.$main.find(".delete-button");
		this.$main.click((e) => {
			if (e.target === this.$deleteButton[0]) {
				if (this.xhr) {
					this.xhr.abort();
				}
				this.onDeleteButtonClick.fire(null);
			} else {
				this.onClick.fire(null)
			}
		});
	}

	public upload(file: File) {
		const formData = new FormData();
		formData.append('files', file, file.name);

		this.$fileName.text(file.name);
		this.$fileSize.text(humanReadableFileSize(file.size, true));

		this.progressIndicator = this.renderAsCircle ? new ProgressCircle(0, {
			circleRadius: 16, circleStrokeWidth: 2
		}) : new ProgressBar(0, {});
		this.progressIndicator.getMainDomElement().appendTo(this.$progressIndicator);

		if (file.size > this.maxBytes) {
			this.progressIndicator.setErrorMessage(this.fileTooLargeMessage);
			this.setState(FileItemState.ERROR);
		} else {
			this.setState(FileItemState.UPLOADING);
			this.xhr = $.ajax({
				url: this.uploadUrl,
				data: formData,
				processData: false,
				contentType: false,
				type: 'POST',
				xhr: () => {
					const xhr = $.ajaxSettings.xhr();
					if (xhr.upload) {
						xhr.upload.addEventListener('progress', (event) => {
							let progress: number;
							if (event.lengthComputable) {
								const position = event.loaded || (event as any).position; // event.position is deprecated
								const total = event.total;
								progress = position / total;
							} else {
								progress = -1;
								FileItem.LOGGER.warn("Cannot calculate percentage progress of file upload!");
							}
							this.progressIndicator.setProgress(progress);
						}, false);
					}
					return xhr;
				},
				success: (fileUuids: string[]) => {
					this.setState(FileItemState.SUCCESS);
					for (let j = 0; j < fileUuids.length; j++) { // it's actually only one uuid, since we upload the files sequentially!
						const fileUuid = fileUuids[j];
						this._uuid = fileUuid;
						this.onUploadSuccess.fire(SubEventFactory.createUiFileField_FileUploadedSubEvent(fileUuid, file.name, file.type, file.size));
					}
				},
				error: () => {
					this.setState(FileItemState.ERROR);
					this.onUploadError.fire(SubEventFactory.createUiFileField_FileUploadErrorSubEvent(file.name, file.type, file.size));
					this.progressIndicator.setErrorMessage(this.uploadErrorMessage);
				},
				complete: () => {
					this.xhr = null;
				}
			});
		}
	}

	public set data(data: any) {
		this._data = data;
		this.$templateWrapper.html(this.renderer.render(data));
		this.setState(FileItemState.DISPLAYING);
	}

	public get data() {
		return this._data;
	}

	public setRenderAsCircle(renderAsCircle: boolean) {
		this.renderAsCircle = renderAsCircle;
		// TODO update
	}

	private setState(state: FileItemState) {
		this._state = state;
		this.$main.removeClass(function (index, classNames) {
			return (classNames.match(/(^|\s)state-\S+/g) || []).join(' ');
		});
		this.$main.addClass("state-" + state);
	}

	public get state() {
		return this._state;
	}

	public get uuid() {
		return this._uuid;
	}

	public getMainDomElement() {
		return this.$main;
	}

}

TeamAppsUiComponentRegistry.registerFieldClass("UiFileField", UiFileField);